<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import GeoAreaMapEditor from './GeoAreaMapEditor.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const showEditor = ref(false);
const editingIndex = ref(null);
const editingArea = ref(null);

const areas = computed({
  get: () => props.modelValue || [],
  set: val => emit('update:modelValue', val),
});

const areaTypeIcon = type => {
  switch (type) {
    case 'bounding_box':
      return 'i-lucide-square';
    case 'polygon':
      return 'i-lucide-pentagon';
    case 'radius':
      return 'i-lucide-circle';
    default:
      return 'i-lucide-map-pin';
  }
};

const areaTypeLabel = type => {
  const key = type?.toUpperCase();
  return t(`LIBRARIES.EDIT.ADVANCED_SETTINGS.CUSTOM_GEO_AREAS.${key}`, type);
};

const addArea = () => {
  editingIndex.value = null;
  editingArea.value = null;
  showEditor.value = true;
};

const editArea = index => {
  editingIndex.value = index;
  editingArea.value = { ...areas.value[index] };
  showEditor.value = true;
};

const deleteArea = index => {
  const updated = [...areas.value];
  updated.splice(index, 1);
  areas.value = updated;
};

const onSave = area => {
  const updated = [...areas.value];
  if (editingIndex.value !== null) {
    updated[editingIndex.value] = area;
  } else {
    updated.push(area);
  }
  areas.value = updated;
  showEditor.value = false;
  editingIndex.value = null;
  editingArea.value = null;
};

const onCancel = () => {
  showEditor.value = false;
  editingIndex.value = null;
  editingArea.value = null;
};
</script>

<template>
  <div>
    <h4
      class="text-sm font-medium text-slate-700 dark:text-slate-300 mt-5 mb-3"
    >
      {{ t('LIBRARIES.EDIT.ADVANCED_SETTINGS.CUSTOM_GEO_AREAS.TITLE') }}
    </h4>
    <p class="text-xs text-slate-500 dark:text-slate-400 mb-3">
      {{ t('LIBRARIES.EDIT.ADVANCED_SETTINGS.CUSTOM_GEO_AREAS.DESCRIPTION') }}
    </p>

    <!-- Area list -->
    <div v-if="areas.length" class="space-y-2 mb-3">
      <div
        v-for="(area, index) in areas"
        :key="index"
        class="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800"
      >
        <div class="flex items-center gap-3 min-w-0">
          <Icon
            :icon="areaTypeIcon(area.area_type)"
            class="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0"
          />
          <div class="min-w-0">
            <div
              class="text-sm font-medium text-slate-700 dark:text-slate-300 truncate"
            >
              {{ area.name }}
            </div>
            <div class="text-xs text-slate-500 dark:text-slate-400">
              {{ areaTypeLabel(area.area_type) }}
              <template v-if="area.description">
                — {{ area.description }}
              </template>
            </div>
          </div>
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <NextButton
            xs
            slate
            ghost
            icon="i-lucide-pencil"
            @click.prevent="editArea(index)"
          />
          <NextButton
            xs
            slate
            ghost
            icon="i-lucide-trash-2"
            @click.prevent="deleteArea(index)"
          />
        </div>
      </div>
    </div>

    <p v-else class="text-xs text-slate-400 dark:text-slate-500 italic mb-3">
      {{ t('LIBRARIES.EDIT.ADVANCED_SETTINGS.CUSTOM_GEO_AREAS.NO_AREAS') }}
    </p>

    <NextButton
      xs
      slate
      faded
      icon="i-lucide-plus"
      :label="t('LIBRARIES.EDIT.ADVANCED_SETTINGS.CUSTOM_GEO_AREAS.ADD_AREA')"
      @click.prevent="addArea"
    />

    <!-- Map editor modal -->
    <GeoAreaMapEditor
      v-if="showEditor"
      :area="editingArea"
      @save="onSave"
      @cancel="onCancel"
    />
  </div>
</template>
